import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import usd from '../../../assets/token-icons/USD.png';
import creditCard from '../../../assets/updated/popup/credit-card.svg';
import wireTransfer from '../../../assets/updated/popup/wiretransfer.svg';
import ach from '../../../assets/updated/popup/ach.png';
import venmo from '../../../assets/updated/popup/venmo.svg';
import paypal from '../../../assets/updated/popup/paypal.svg';
import zelle from '../../../assets/updated/popup/zelle.svg';
import tygpay from '../../../assets/updated/tyga_icon.png';
import transactionIcon from '../../../assets/updated/buySell/transactionMethod.svg';

import { decodeJWT, getUserWallets } from '../../../services/api';

const useStyles = makeStyles((theme) => ({
  dataShow: {
    opacity: '1 !important',
    visibility: 'visible !important',
    '& .bnModalWrap': { transform: 'scale(1) !important' },
  },
  bnMask: {
    backgroundColor: 'rgba(0, 0, 0, .6)',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 1200,
    width: '100%',
    height: '100vh',
  },
  bnModal: {
    '& .bnModalWrap': {
      backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#1e2329',
      borderRadius: '16px',
      boxShadow: '0px 3px 6px rgba(0,0,0,.1)',
      overflow: 'hidden',
      position: 'relative',
      transform: 'scale(.95)',
      transition: 'all 250ms ease-in-out',
      maxWidth: '520px',
      width: '100%',
      marginTop: '60px',
    },
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.text.primary,
  },
  header: {
    padding: '20px 24px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& h3': { margin: 0, fontSize: 20, fontWeight: 600 },
  },
  scroll: {
    padding: 24,
    maxHeight: '60vh',
    overflow: 'auto',
    '&::-webkit-scrollbar': { width: 7 },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? '#5f6673' : '#b7bdc6',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-track': { display: 'none' },
  },
  button: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 16px',
    background: 'transparent',
    marginBottom: '12px',
    border: `1px solid ${
      theme.palette.mode === 'light' ? '#E0E3E7' : '#2D3843'
    }`,
    borderRadius: 8,
    textTransform: 'none',
    width: '100%',
    cursor: 'pointer',
    '&:hover': { borderColor: theme.palette.primary.main },
  },
  iconText: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    '& img': { width: 42, height: 42, objectFit: 'contain' },
  },
  btnText: { fontSize: 16, color: theme.palette.text.primary },
  usdBalanceHint: { marginLeft: 6, opacity: 0.7, fontSize: 13 },
}));

const DepositPaymentPopup = ({
  open,
  onClose,
  amount = 0,
  onSelectPaymentMethod,
  type = 'Deposit',
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [usdBalance, setUsdBalance] = useState('0.00');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = localStorage.getItem('access_token');

        // Check if token exists and is valid
        if (!token || token === 'null' || token === 'undefined') {
          console.log('No valid token found, skipping wallet fetch');
          return;
        }

        const decoded = decodeJWT(String(token));

        // Check if token was successfully decoded and has email
        if (!decoded || !decoded.email) {
          console.log('Invalid token or missing email, skipping wallet fetch');
          return;
        }

        const wallets = await getUserWallets(decoded.email);
        const usd =
          wallets?.data?.find((x) => x.coinSymbol === 'USD')?.coinBalance || 0;
        setUsdBalance(
          usd < 0.001
            ? usd.toLocaleString('en-US', {
                minimumFractionDigits: 5,
                maximumFractionDigits: 6,
              })
            : usd.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
        );
      } catch {
        // ignore
      }
    })();
  }, [open]);

  if (!open) return null;

  const choose = (method) => {
    onSelectPaymentMethod?.(method);
  };

  return (
    <div
      className={`${classes.bnMask} ${classes.bnModal} ${
        open ? classes.dataShow : ''
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Select payment method"
    >
      <div className="bnModalWrap">
        <div className={classes.contentContainer}>
          <div className={classes.header}>
            <div />
            <h3>Pay With</h3>
            <div
              onClick={onClose}
              style={{ cursor: 'pointer' }}
              aria-label="Close"
            >
              <CloseIcon
                sx={{ '&:hover': { color: theme.palette.text.primary } }}
              />
            </div>
          </div>

          <div className={classes.scroll}>
            {/* Card / PayPal */}
            <button
              className={classes.button}
              onClick={() => choose('Credit Card')}
            >
              <Box className={classes.iconText}>
                <img src={creditCard} alt="Credit Card" />
                <p className={classes.btnText}>Credit Card</p>
              </Box>
              <p>${amount.toLocaleString()}</p>
            </button>
            <button className={classes.button} onClick={() => choose('Paypal')}>
              <Box className={classes.iconText}>
                <img src={paypal} alt="PayPal" />
                <p className={classes.btnText}>PayPal</p>
              </Box>
              <p>${amount.toLocaleString()}</p>
            </button>

            {/* Bank-like */}
            <button className={classes.button} onClick={() => choose('ACH')}>
              <Box className={classes.iconText}>
                <img src={ach} alt="ACH" />
                <p className={classes.btnText}>ACH</p>
              </Box>
              <p>${amount.toLocaleString()}</p>
            </button>
            <button
              className={classes.button}
              onClick={() => choose('Wire Transfer')}
            >
              <Box className={classes.iconText}>
                <img src={wireTransfer} alt="Wire Transfer" />
                <p className={classes.btnText}>Wire Transfer</p>
              </Box>
              <p>${amount.toLocaleString()}</p>
            </button>
            <button className={classes.button} onClick={() => choose('Zelle')}>
              <Box className={classes.iconText}>
                <img src={zelle} alt="Zelle" />
                <p className={classes.btnText}>Zelle</p>
              </Box>
              <p>${amount.toLocaleString()}</p>
            </button>

            {/* Wallet/alt rails */}
            <button
              className={classes.button}
              onClick={() => choose('TygaPay')}
            >
              <Box className={classes.iconText}>
                <img src={tygpay} alt="TygaPay" />
                <p className={classes.btnText}>TygaPay</p>
              </Box>
              <p>${amount.toLocaleString()}</p>
            </button>
            <button className={classes.button} onClick={() => choose('Venmo')}>
              <Box className={classes.iconText}>
                <img src={venmo} alt="Venmo" />
                <p className={classes.btnText}>Venmo</p>
              </Box>
              <p>${amount.toLocaleString()}</p>
            </button>

            {/* (Optional) Asset Wallet (more relevant for Sell flows) */}
            {type === 'Sell' && (
              <button
                className={classes.button}
                onClick={() => choose('Asset Wallet')}
              >
                <Box className={classes.iconText}>
                  <img src={transactionIcon} alt="Asset Wallet" />
                  <p className={classes.btnText}>Asset Wallet</p>
                </Box>
                <p>${amount.toLocaleString()}</p>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositPaymentPopup;
